import React, { useMemo, useRef, useState, useEffect } from "react";
import ScrollToBottom from "../common/ScrollToBottom";
import MessageList from "../messages/MessageList";
import EditMessageModal from "../messages/EditMessageModal";
import ChatSidebar from "../layout/ChatSidebar";
import ChatList from "../layout/ChatList";
import ChatListItem from "../layout/ChatListItem";
import ChatFooter from "../input/ChatFooter";
import { useSearchParams } from "react-router-dom";
import {
  CreateGroupModal,
  GroupHeaderActions,
  GroupInfoModal,
  AddMembersModal,
} from "../groups";
import { useChat } from "../contexts/ChatContext";
import { useUsers } from "../contexts/UserContext";
import { useSocket } from "../contexts/SocketContext";
import { SOCKET_EVENTS } from "../socket/socketEvents";
import ChatMediaApi from "../../../api/ChatApi";
import ConversationApi from "../../../api/ConversationApi";

function ChatWindow() {
  const socket = useSocket();

  const [searchParams] = useSearchParams();

  const leadId = searchParams.get("leadId");

  useEffect(() => {
    if (leadId) {
      openConversationByLead(leadId);
    }
  }, [leadId]);

  const {
    conversations,
    activeConversation,
    messages,
    currentUserId,
    joinConversation,
    openConversationByLead,
    sendMessage,
    deleteMessage,
    startTyping,
    stopTyping,
    createGroup,
    addMembers,
    removeMember,
    leaveGroup,
    renameGroup,
    transferOwnership,
    addReaction,
    replyTo,
    setReplyTo,
    editMessage,
    typingUsers,
    loadMoreMessages,
  } = useChat();

  const { users = [] } = useUsers();

  const [message, setMessage] = useState("");
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [editMessageData, setEditMessageData] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [messageSearch, setMessageSearch] = useState("");

  const chatBodyRef = useRef(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);

  useEffect(() => {
    if (leadId) {
      openConversationByLead(leadId);
    }
  }, [leadId]);

  const downloadImage = () => {
    if (!previewImg) return;

    const link = document.createElement("a");

    link.href = previewImg;

    const fileName = previewImg.split("/").pop() || "chat-image.png";

    link.download = fileName;

    link.target = "_blank"; // prevent same tab open

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  };

  const handleScroll = () => {
    const el = chatBodyRef.current;
    if (!el) return;

    const isBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;

    setIsNearBottom(isBottom);
    setShowScrollBtn(!isBottom);

    if (el.scrollTop <= 10 && loadMoreMessages) {
      loadMoreMessages();
    }
  };

  useEffect(() => {
    const el = chatBodyRef.current;
    if (!el) return;

    el.addEventListener("scroll", handleScroll);

    return () => {
      el.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!chatBodyRef.current) return;

    if (isNearBottom) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    } else {
      setShowScrollBtn(true);
    }
  }, [messages]);

  /* ================= USERS MAP ================= */
  const usersMap = useMemo(() => {
    const map = {};
    users.forEach((u) => (map[String(u._id)] = u));
    return map;
  }, [users]);

  const typingNames = Object.keys(typingUsers || {})
    .map((id) => usersMap[id]?.name)
    .filter(Boolean);

  /* ================= SIDEBAR DISPLAY NAME ================= */
  const getDisplayDetails = (conv) => {
    if (!conv || !Array.isArray(conv.members)) {
      return { name: "Unknown", avatar: "" };
    }

    if (conv.type === "group") {
      return {
        name: conv.name || "Group Chat",
        avatar: conv.groupIcon || "",
      };
    }

    const otherMember = conv.members.find((m) => {
      if (!m?.userId) return false;

      const uid = typeof m.userId === "object" ? m.userId._id : m.userId;
      return uid && String(uid) !== String(currentUserId);
    });

    if (!otherMember || !otherMember.userId) {
      return { name: "Deleted User", avatar: "" };
    }

    const user =
      typeof otherMember.userId === "object"
        ? otherMember.userId
        : usersMap[String(otherMember.userId)];

    return {
      name: user?.name || user?.email || "Deleted User",
      avatar: user?.avatar
        ? `http://localhost:5000/uploads/${user.avatar}`
        : "",
    };
  };

  /* ================= FILTERED CONVERSATIONS ================= */

  const filteredConversations = useMemo(() => {
    return (conversations || []).filter((conv) => {
      if (!conv || !conv.members?.length) return false;

      const { name } = getDisplayDetails(conv);

      return name?.toLowerCase().includes(searchTerm.toLowerCase().trim());
    });
  }, [conversations, searchTerm, currentUserId]);

  const filteredMessages = useMemo(() => {
    return (messages || []).filter((msg) =>
      msg.text?.toLowerCase().includes(messageSearch.toLowerCase().trim()),
    );
  }, [messages, messageSearch]);

  /* ================= FILE UPLOAD ================= */

  const handleFileSelect = async (file, type) => {
    if (!activeConversation) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("conversationId", activeConversation._id);
    formData.append("type", type);

    try {
      const res = await ChatMediaApi.upload(formData);

      console.log("UPLOAD RESPONSE:", res.data);

      const mediaUrl = res?.data?.url;

      if (!mediaUrl) {
        console.error("Media URL missing");
        return;
      }

      // 🔥 SEND MESSAGE THROUGH SOCKET
      sendMessage({
        type: type,
        fileUrl: mediaUrl,
        text: "",
        replyTo: replyTo?._id || null,
      });
    } catch (err) {
      console.error("Upload failed", err);
    }
  };

  return (
    // <div className="chat-wrapper d-flex w-100 h-100 bg-white">
    //   {previewImg && (
    //     <div className="image-viewer">
    //       <div className="image-viewer-content">
    //         <img src={previewImg} alt="preview" />

    //         <div className="image-actions">
    //           <button className="btn btn-primary" onClick={downloadImage}>
    //             Download
    //           </button>

    //           <button
    //             className="btn btn-danger"
    //             onClick={() => setPreviewImg(null)}
    //           >
    //             Close
    //           </button>
    //         </div>
    //       </div>
    //     </div>
    //   )}
    //   {/* ================= SIDEBAR ================= */}
    //   <ChatSidebar>
    //     <div className="d-flex align-items-center gap-2 p-3 border-bottom bg-white">
    //       {/* SEARCH BOX */}
    //       <div className="position-relative flex-grow-1">
    //         <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>

    //         <input
    //           type="text"
    //           className="form-control ps-5 rounded-pill shadow-sm"
    //           placeholder="Search chats..."
    //           value={searchTerm}
    //           onChange={(e) => setSearchTerm(e.target.value)}
    //         />
    //       </div>

    //       {/* CREATE GROUP BUTTON */}
    //       <button
    //         className="btn btn-primary rounded-pill px-3 d-flex align-items-center gap-1"
    //         onClick={() => setShowCreateGroup(true)}
    //       >
    //         <i className="ti ti-plus"></i>
    //         <span>Create</span>
    //       </button>
    //     </div>

    //     <ChatList empty={filteredConversations.length === 0}>
    //       {filteredConversations.map((conv) => {
    //         const { name, avatar } = getDisplayDetails(conv);
    //         return (
    //           <ChatListItem
    //             key={conv._id}
    //             name={name}
    //             avatar={avatar}
    //             isGroup={conv.type === "group"}
    //             unreadCount={conv.unreadCount || 0}
    //             lastMessage={conv.lastMessage?.text}
    //             lastMessageType={conv.lastMessage?.type}
    //             time={
    //               conv.lastMessage?.createdAt
    //                 ? new Date(conv.lastMessage.createdAt).toLocaleTimeString(
    //                     [],
    //                     {
    //                       hour: "2-digit",
    //                       minute: "2-digit",
    //                     },
    //                   )
    //                 : ""
    //             }
    //             onClick={() => joinConversation(conv)}
    //           />
    //         );
    //       })}
    //       {users
    //         .filter(
    //           (u) =>
    //             !conversations.some(
    //               (c) =>
    //                 c.type === "private" &&
    //                 c.members.some(
    //                   (m) =>
    //                     String(m.userId?._id || m.userId) === String(u._id),
    //                 ),
    //             ),
    //         )
    //         .map((u) => (
    //           <ChatListItem
    //             key={`user-${u._id}`}
    //             name={u.name || u.email}
    //             avatar={u.avatar}
    //             isGroup={false}
    //             onClick={async () => {
    //               const res = await ConversationApi.startConversation(u._id);
    //               socket.emit("privateConversationCreated", {
    //                 conversationId: res.data._id,
    //               });
    //               joinConversation(res.data);
    //             }}
    //           />
    //         ))}
    //     </ChatList>
    //   </ChatSidebar>

    //   {/* ================= CHAT PANEL ================= */}
    //   <div className="chat flex-grow-1 d-flex flex-column">
    //     <div className="chat-header d-flex justify-content-between align-items-center p-3 border-bottom bg-white">
    //       <div>
    //         <h6 className="mb-0 fw-bold">
    //           {activeConversation
    //             ? getDisplayDetails(activeConversation).name
    //             : "Select a Chat"}
    //         </h6>

    //         {typingNames.length > 0 && (
    //           <small className="text-primary">
    //             {typingNames.join(", ")} typing...
    //           </small>
    //         )}
    //       </div>

    //       {/* MESSAGE SEARCH */}
    //       {activeConversation && (
    //         <div className="position-relative" style={{ width: "220px" }}>
    //           <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-2 text-muted"></i>

    //           <input
    //             type="text"
    //             className="form-control ps-4 rounded-pill"
    //             placeholder="Search messages..."
    //             value={messageSearch}
    //             onChange={(e) => setMessageSearch(e.target.value)}
    //           />
    //         </div>
    //       )}
    //     </div>

    //     <div
    //       className="chat-body flex-grow-1 p-3 overflow-auto"
    //       ref={chatBodyRef}
    //     >
    //       {activeConversation ? (
    //         <MessageList
    //           messages={filteredMessages}
    //           currentUserId={currentUserId}
    //           isGroup={activeConversation?.type === "group"}
    //           onDelete={deleteMessage}
    //           onEdit={setEditMessageData}
    //           onReply={setReplyTo}
    //           onReact={addReaction}
    //           onImageClick={setPreviewImg}
    //         />
    //       ) : (
    //         <div className="h-100 d-flex align-items-center justify-content-center text-muted">
    //           Pick a chat to start messaging
    //         </div>
    //       )}
    //     </div>

    //     {activeConversation && (
    //       <ChatFooter
    //         message={message}
    //         setMessage={setMessage}
    //         replyTo={replyTo}
    //         onCancelReply={() => setReplyTo(null)}
    //         onTyping={() => startTyping(activeConversation._id)}
    //         onStopTyping={() => stopTyping(activeConversation._id)}
    //         onFileSelect={handleFileSelect}
    //         onSend={() => {
    //           if (message.trim()) {
    //             stopTyping(activeConversation._id);

    //             sendMessage({
    //               type: "text",
    //               text: message,
    //               replyTo: replyTo?._id,
    //             });

    //             setMessage("");

    //             // ✅ CLOSE REPLY PREVIEW AFTER SEND
    //             setReplyTo(null);
    //           }
    //         }}
    //       />
    //     )}
    //   </div>

    //   {/* ================= MODALS ================= */}
    //   {showCreateGroup && (
    //     <CreateGroupModal
    //       users={users}
    //       leadId={leadId || null}
    //       onCreate={(name, members, leadId) => {
    //         createGroup({
    //           name,
    //           members,
    //           leadId,
    //         });

    //         setShowCreateGroup(false);
    //       }}
    //       onClose={() => setShowCreateGroup(false)}
    //     />
    //   )}

    //   {showGroupInfo && activeConversation && (
    //     <GroupInfoModal
    //       group={activeConversation}
    //       usersMap={usersMap}
    //       currentUserId={currentUserId}
    //       onAddMembers={() => {
    //         setShowGroupInfo(false);
    //         setShowAddMembers(true);
    //       }}
    //       onRemoveMember={(uid) => removeMember(activeConversation._id, uid)}
    //       onLeaveGroup={() =>
    //         window.confirm("Leave group?") && leaveGroup(activeConversation._id)
    //       }
    //       onRenameGroup={(n) => renameGroup(activeConversation._id, n)}
    //       onTransferOwnership={(u) =>
    //         transferOwnership(activeConversation._id, u)
    //       }
    //       onClose={() => setShowGroupInfo(false)}
    //     />
    //   )}

    //   {showAddMembers && activeConversation && (
    //     <AddMembersModal
    //       users={users.filter(
    //         (u) =>
    //           !activeConversation.members.some(
    //             (m) => String(m.userId?._id || m.userId) === String(u._id),
    //           ),
    //       )}
    //       onAdd={(ids) => {
    //         addMembers(activeConversation._id, ids);
    //         setShowAddMembers(false);
    //       }}
    //       onClose={() => setShowAddMembers(false)}
    //     />
    //   )}

    //   {editMessageData && (
    //     <EditMessageModal
    //       message={editMessageData}
    //       onSave={(text) => {
    //         editMessage(editMessageData._id, text);
    //         setEditMessageData(null);
    //       }}
    //       onClose={() => setEditMessageData(null)}
    //     />
    //   )}

    //   <ScrollToBottom
    //     visible={showScrollBtn}
    //     onClick={() => {
    //       chatBodyRef.current?.scrollTo({
    //         top: chatBodyRef.current.scrollHeight,
    //         behavior: "smooth",
    //       });
    //       setShowScrollBtn(false);
    //     }}
    //   />
    // </div>

     <div className="modern-chat-layout">

    {/* IMAGE VIEWER */}
    {previewImg && (
      <div className="modern-image-viewer">
        <div className="modern-image-viewer-content">
          <img src={previewImg} alt="preview" />

          <div className="modern-image-actions">
            <button className="btn btn-light" onClick={downloadImage}>
              Download
            </button>

            <button
              className="btn btn-danger"
              onClick={() => setPreviewImg(null)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}

    {/* SIDEBAR */}
    <div className="modern-sidebar">

      <div className="modern-sidebar-top">

        <div className="modern-search-box">
          <i className="ti ti-search"></i>

          <input
            type="text"
            placeholder="Search chats..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <button
          className="modern-create-btn"
          onClick={() => setShowCreateGroup(true)}
        >
          <i className="ti ti-plus"></i>
          Create
        </button>
      </div>

      <div className="modern-chat-list">
        <ChatList empty={filteredConversations.length === 0}>

          {filteredConversations.map((conv) => {
            const { name, avatar } = getDisplayDetails(conv);

            return (
              <ChatListItem
                key={conv._id}
                name={name}
                avatar={avatar}
                 online={conv?.onlineUsers?.length > 0}
                isGroup={conv.type === "group"}
                unreadCount={conv.unreadCount || 0}
                lastMessage={conv.lastMessage?.text}
                lastMessageType={conv.lastMessage?.type}
                time={
                  conv.lastMessage?.createdAt
                    ? new Date(conv.lastMessage.createdAt).toLocaleTimeString(
                        [],
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )
                    : ""
                }
                onClick={() => joinConversation(conv)}
              />
            );
          })}

        </ChatList>
      </div>
    </div>

    {/* CHAT PANEL */}
    <div className="modern-chat-panel">

      {/* HEADER */}
      <div className="modern-chat-header">

        <div className="modern-chat-user">

          <div className="modern-chat-avatar">
            {activeConversation
              ? getDisplayDetails(activeConversation).name?.charAt(0)
              : "C"}
          </div>

          <div>
            <h5>
              {activeConversation
                ? getDisplayDetails(activeConversation).name
                : "Select a Chat"}
            </h5>

            {typingNames.length > 0 && (
              <span className="typing-animation">
                {typingNames.join(", ")} typing...
              </span>
            )}
          </div>
        </div>

        {activeConversation && (
          <div className="modern-message-search">

            <i className="ti ti-search"></i>

            <input
              type="text"
              placeholder="Search messages..."
              value={messageSearch}
              onChange={(e) => setMessageSearch(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* BODY */}
      <div
        className="modern-chat-body"
        ref={chatBodyRef}
      >
        {activeConversation ? (
          <MessageList
            messages={filteredMessages}
            currentUserId={currentUserId}
            isGroup={activeConversation?.type === "group"}
            onDelete={deleteMessage}
            onEdit={setEditMessageData}
            onReply={setReplyTo}
            onReact={addReaction}
            onImageClick={setPreviewImg}
          />
        ) : (
          <div className="modern-empty-chat">
            <div className="modern-empty-icon">
              💬
            </div>

            <h3>Select a conversation</h3>

            <p>
              Start chatting with your team members
            </p>
          </div>
        )}
      </div>

      {/* FOOTER */}
      {activeConversation && (
        <ChatFooter
          message={message}
          setMessage={setMessage}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          onTyping={() => startTyping(activeConversation._id)}
          onStopTyping={() => stopTyping(activeConversation._id)}
          onFileSelect={handleFileSelect}
          onSend={() => {
            if (message.trim()) {
              stopTyping(activeConversation._id);

              sendMessage({
                type: "text",
                text: message,
                replyTo: replyTo?._id,
              });

              setMessage("");
              setReplyTo(null);
            }
          }}
        />
      )}
    </div>

    {/* MODALS */}
    {showCreateGroup && (
      <CreateGroupModal
        users={users}
        leadId={leadId || null}
        onCreate={(name, members, leadId) => {
          createGroup({
            name,
            members,
            leadId,
          });

          setShowCreateGroup(false);
        }}
        onClose={() => setShowCreateGroup(false)}
      />
    )}

    <ScrollToBottom
      visible={showScrollBtn}
      onClick={() => {
        chatBodyRef.current?.scrollTo({
          top: chatBodyRef.current.scrollHeight,
          behavior: "smooth",
        });

        setShowScrollBtn(false);
      }}
    />
  </div>
  );
}

export default ChatWindow;
